import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { AuthService, BUYER_ROLE, BUYER_STATUS } from '../shared';
import { BaseUser } from '../shared/auth/dto/base-user';
import {
  PASSWORD_CHANGE_ACCOUNT_QUERY,
} from '../common/libs/password-policy.util';
import { BuyerService } from './buyer.service';
import {
  PasswordExpiryDialogComponent,
  PasswordExpiryDialogResult,
} from '../modules/resp/_component/password-expiry-dialog/password-expiry-dialog.component';

/**
 * 로그인 / 앱 시작 시 90일 비밀번호 변경 유도 다이얼로그 코디네이터.
 * BE: GET /buyer 의 needChangePwd 플래그로 노출 여부 판단.
 * - Change Password → /my/account?changePassword=1 (비밀번호 변경 폼 자동 오픈)
 * - Remind Me Later → POST /buyer/password/change/defer (만료일 +90일)
 */
@Injectable({ providedIn: 'root' })
export class PasswordExpiryFlowService {
  private authService = inject(AuthService);
  private buyerService = inject(BuyerService);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  private isChecking = false;
  private isDialogOpen = false;
  private checkApiCalled = false;

  checkOnceOnAppStart(panelClassPrefix: 'pc' | 'mobile' = 'pc'): void {
    this.runCheckOnce(panelClassPrefix);
  }

  checkOnceOnLogin(panelClassPrefix: 'pc' | 'mobile' = 'pc'): void {
    this.checkApiCalled = false;
    this.runCheckOnce(panelClassPrefix);
  }

  resetSession(): void {
    this.checkApiCalled = false;
    this.isChecking = false;
    this.isDialogOpen = false;
  }

  private runCheckOnce(panelClassPrefix: 'pc' | 'mobile'): void {
    if (this.checkApiCalled || this.isChecking || this.isDialogOpen) {
      return;
    }

    const user = this.authService.getUser();
    if (!user) {
      this.resetSession();
      return;
    }

    if (!this.isEligible(user) || this.shouldSkipRoute()) {
      return;
    }

    this.checkApiCalled = true;
    this.isChecking = true;
    this.buyerService
      .getBuyer()
      .pipe(finalize(() => (this.isChecking = false)))
      .subscribe({
        next: (buyer) => {
          if (buyer?.needChangePwd === true && !this.shouldSkipRoute()) {
            this.openDialog(panelClassPrefix);
          }
        },
        error: () => {},
      });
  }

  private openDialog(panelClassPrefix: 'pc' | 'mobile'): void {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;
    const isMobile = panelClassPrefix === 'mobile';

    const ref = this.dialog.open(PasswordExpiryDialogComponent, {
      panelClass: [panelClassPrefix, 'dialog', 'password-expiry-dialog'],
      autoFocus: false,
      disableClose: true,
      width: isMobile ? 'calc(100vw - 32px)' : '480px',
      maxWidth: 'calc(100vw - 32px)',
    });

    ref.afterClosed().subscribe((result: PasswordExpiryDialogResult) => {
      this.isDialogOpen = false;
      if (result === 'change') {
        void this.router.navigate(['/my/account'], { queryParams: PASSWORD_CHANGE_ACCOUNT_QUERY });
        return;
      }

      // Remind Me Later → 만료일 +90일 미루기
      this.buyerService.deferPasswordChange().subscribe({ error: () => {} });
    });
  }

  private isEligible(user: BaseUser): boolean {
    if (!this.authService.getId() || !user.userRoles?.includes(BUYER_ROLE.BUYER_APPROVED)) {
      return false;
    }

    const statuses = Array.isArray(user.buyerStatus) ? user.buyerStatus : [user.buyerStatus];
    return statuses.includes(BUYER_STATUS.APPROVAL);
  }

  private shouldSkipRoute(): boolean {
    const path = this.router.url.split('?')[0];
    const skipPrefixes = ['/signin', '/signup', '/withdraw', '/not-found'];
    return skipPrefixes.some((prefix) => path.startsWith(prefix));
  }
}
